import { useMemo, useState } from 'react';
import { useCrm } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { Users, Search, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';

const formatDateTimeFR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

type Mode = 'actif' | 'passif';

export default function SuiviClients({ embedded = false }: { embedded?: boolean } = {}) {
  const data = useCrm();
  const confirm = useConfirm();
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<Mode>('actif');

  const grouped = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayTs = startOfToday.getTime();

    const map = new Map<string, typeof data.prest>();
    for (const p of data.prest) {
      if (p.archivedAt) continue;
      const ts = p.dateEvent ? new Date(p.dateEvent).getTime() : null;
      const isPast = ts != null && !isNaN(ts) && ts < todayTs;
      if (mode === 'actif' && isPast) continue;
      if (mode === 'passif' && !isPast) continue;
      const name = (p.client || '—').trim() || '—';
      const arr = map.get(name) ?? [];
      arr.push(p);
      map.set(name, arr);
    }
    return Array.from(map.entries())
      .map(([name, prests]) => {
        const sorted = [...prests].sort((a, b) => {
          const ta = a.dateEvent ? new Date(a.dateEvent).getTime() : null;
          const tb = b.dateEvent ? new Date(b.dateEvent).getTime() : null;
          if (mode === 'actif') {
            if (ta == null) return 1;
            if (tb == null) return -1;
            return ta - tb;
          }
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta;
        });
        const ca = prests.reduce((s, p) => s + (p.propTTC ?? 0), 0);
        return { name, prests: sorted, ca };
      })
      .sort((a, b) => b.ca - a.ca);
  }, [data.prest, mode]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return grouped;
    return grouped.filter((g) => g.name.toLowerCase().includes(needle));
  }, [grouped, q]);

  const totalCA = grouped.reduce((s, g) => s + g.ca, 0);
  const totalPrest = grouped.reduce((s, g) => s + g.prests.length, 0);

  return (
    <div>
      {!embedded && (
        <PageTitle
          title="Suivi Clients"
          subtitle="Activité et historique par client"
          icon={<Users className="text-amber-400" size={24} strokeWidth={1.5} />}
        />
      )}

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setMode('actif')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'actif' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
          }`}
        >
          Actifs (à venir)
        </button>
        <button
          onClick={() => setMode('passif')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'passif' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
          }`}
        >
          Passifs (passés)
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Clients</div>
          <div className="font-heading text-3xl text-amber-400">{grouped.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">{mode === 'actif' ? 'Prestations à venir' : 'Prestations passées'}</div>
          <div className="font-heading text-3xl text-sky-400">{totalPrest}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Total prestations</div>
          <div className="font-heading text-3xl text-violet-400">{data.prest.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">CA</div>
          <div className="font-heading text-3xl text-emerald-400">{formatEUR(totalCA)}</div>
        </Card>
      </div>


      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un client…"
          className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/50"
        />
      </div>

      <div className="space-y-4">
        {filtered.map(({ name, prests, ca }) => {
          const services = Array.from(new Set(prests.flatMap((p) => p.services || [])));
          const lastDate = prests[0]?.dateEvent;
          const signed = prests.filter((p) => p.statut === 'Signé' || p.statut === 'Réalisée').length;
          return (
            <Card key={name} className="overflow-hidden">
              <div className="p-4 border-b border-slate-800 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-lg text-slate-100 flex items-center gap-2">
                    <Users size={16} className="text-amber-400" strokeWidth={1.5} /> {name}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {services.slice(0, 6).map((s) => (
                      <Tag key={s} color="sky">{s}</Tag>
                    ))}
                  </div>
                  {lastDate && (
                    <div className="text-[10px] text-slate-500 mt-2">
                      Dernière&nbsp;: <span className="text-slate-300">{formatDateFR(lastDate)}</span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">CA cumulé</div>
                  <div className="text-amber-400 font-heading text-lg">{formatEUR(ca)}</div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    PRESTA <span className="text-slate-300 font-medium">{prests.length}</span> · SIGNÉS{' '}
                    <span className="text-emerald-400 font-medium">{signed}</span>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-2 text-left">Créée le</th>
                      <th className="px-4 py-2 text-left">Date event</th>
                      <th className="px-4 py-2 text-left">Libellé</th>
                      <th className="px-4 py-2 text-left">Statut</th>
                      <th className="px-4 py-2 text-left">Lieu</th>
                      <th className="px-4 py-2 text-left">Artistes</th>
                      <th className="px-4 py-2 text-right">Prop. TTC</th>
                      <th className="px-4 py-2 text-right w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {prests.map((p) => (
                      <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                        <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{formatDateTimeFR(p.dateCreation)}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{formatDateFR(p.dateEvent)}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-300">{p.libelle}</td>
                        <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{p.lieu || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-300">{p.artistes?.join(', ') || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-right text-amber-400">
                          {p.propTTC != null ? formatEUR(p.propTTC) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <button
                            title="Archiver cette proposition"
                            onClick={async () => {
                              const ok = await confirm({
                                title: 'Archiver cette proposition ?',
                                description: `Elle disparaîtra du suivi actif (visible dans Archives). ${p.libelle || ''}`,
                                confirmLabel: 'Archiver',
                              });
                              if (!ok) return;
                              await (data as any).archivePrest(p.id);
                              toast.success('Proposition archivée');
                            }}
                            className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-colors"
                          >
                            <Archive size={14} strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="p-8 text-center text-sm text-slate-500">Aucun client trouvé.</Card>
        )}
      </div>
    </div>
  );
}
